import { queryOptions } from '@tanstack/react-query'
import { queryKeys } from '../keys'
import { selectionBookingDetail } from '../selects'
import { transformDetailTransportation } from '../selects/transportation'
import type { UnusedSkipTokenOptions } from '@tanstack/react-query'
import type { HTTPError } from 'ky'
import type { HttpErrorResponse } from '@/types/error'
import type {
  GetListDateTypesResponse,
  GetListDatesResponse,
  GetListTimeResponse,
  GetListTimeSlotResponse,
  GetListTimeTypesResponse,
} from '@/types/booking'
import type {
  GetListDateTypesSchema,
  GetListDatesSchema,
  GetListTimeSchema,
  GetListTimeSlotSchema,
  GetListTimeTypesSchema,
} from '@/schemas/booking'
import { bookingService } from '@/services/booking'

export const getBookingQueryOptions = (
  params: Parameters<typeof bookingService.detail>[0],
  kyOptions?: Parameters<typeof bookingService.detail>[1],
  options?: Omit<
    UnusedSkipTokenOptions<
      Awaited<ReturnType<typeof bookingService.detail>>,
      HTTPError<HttpErrorResponse>,
      ReturnType<typeof selectionBookingDetail>,
      ReturnType<typeof queryKeys.booking.detail>
    >,
    'queryKey' | 'queryFn'
  >,
) =>
  queryOptions({
    queryKey: queryKeys.booking.detail(params),
    queryFn: ({ signal }) =>
      bookingService.detail(params, { signal, ...kyOptions }),
    enabled: !!params,
    select: selectionBookingDetail,
    ...options,
  })

export const getBookingTransportationQueryOptions = (
  params: Parameters<typeof bookingService.transportation>[0],
) =>
  queryOptions({
    queryKey: queryKeys.booking.transportation(params),
    queryFn: ({ signal }) => bookingService.transportation(params, { signal }),
    enabled: !!params,
    select: transformDetailTransportation,
  })

export const getBookingPlanQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.booking.plan(),
    queryFn: ({ signal }) => bookingService.plan({ signal }),
  })
export const getListDatesQueryOptions = (
  params: GetListDatesSchema,
  options?: Omit<
    UnusedSkipTokenOptions<
      GetListDatesResponse,
      HTTPError<HttpErrorResponse>,
      GetListDatesResponse,
      ReturnType<typeof queryKeys.booking.date>
    >,
    'queryKey' | 'queryFn'
  >,
) =>
  queryOptions({
    queryKey: queryKeys.booking.date(params),
    queryFn: () => {
      const searchParams = params.month && {
        month: params.month,
      }
      return bookingService.getListDates(params.id, { searchParams })
    },
    ...options,
  })

export const getListDateTypesQueryOptions = (
  params: GetListDateTypesSchema,
  options?: Exclude<
    UnusedSkipTokenOptions<
      GetListDateTypesResponse,
      HTTPError<HttpErrorResponse>,
      GetListDateTypesResponse,
      ReturnType<typeof queryKeys.booking.dateTypes>
    >,
    'queryKey' | 'queryFn'
  >,
) =>
  queryOptions({
    queryKey: queryKeys.booking.dateTypes(params),
    queryFn: () => {
      const searchParams = params.month && {
        month: params.month,
      }
      return bookingService.getListDateTypes(params.id, {
        searchParams,
      })
    },
    ...options,
  })

export const getListTimeQueryOptions = (
  params: GetListTimeSchema,
  options?: Omit<
    UnusedSkipTokenOptions<
      GetListTimeResponse,
      HTTPError<HttpErrorResponse>,
      GetListTimeResponse,
      ReturnType<typeof queryKeys.booking.time>
    >,
    'queryKey' | 'queryFn'
  >,
) =>
  queryOptions({
    queryKey: queryKeys.booking.time(params),
    queryFn: () => {
      return bookingService.getListTime(params.id)
    },
    ...options,
  })

export const getListTimeTypesQueryOptions = (
  params: GetListTimeTypesSchema,
  options?: Exclude<
    UnusedSkipTokenOptions<
      GetListTimeTypesResponse,
      HTTPError<HttpErrorResponse>,
      GetListTimeTypesResponse,
      ReturnType<typeof queryKeys.booking.timeTypes>
    >,
    'queryKey' | 'queryFn'
  >,
) =>
  queryOptions({
    queryKey: queryKeys.booking.timeTypes(params),
    queryFn: () => {
      const searchParams = params.time_slot && {
        time_slot: params.time_slot,
      }
      return bookingService.getListTimeTypes(params.id, {
        searchParams,
      })
    },
    ...options,
  })

export const getListTimeSlotQueryOptions = (
  params: GetListTimeSlotSchema,
  options?: Omit<
    UnusedSkipTokenOptions<
      GetListTimeSlotResponse,
      HTTPError<HttpErrorResponse>,
      GetListTimeSlotResponse,
      ReturnType<typeof queryKeys.booking.timeSlot>
    >,
    'queryKey' | 'queryFn'
  >,
) =>
  queryOptions({
    queryKey: queryKeys.booking.timeSlot(params),
    queryFn: () => {
      return bookingService.getListTimeSlot(params)
    },
    ...options,
  })
